import { ArrowLeft, ArrowRight, Loader2, X } from 'lucide-react';
import { UploadedMethodType, useOnboardingStore } from '../../store/onboardingStore';
import { StepIndicator } from './ui/step-indicator';
import { StepProductData } from './steps/step-1-product-data';
import { StepVisualContent } from './steps/step-2-visual-content';
import { StepAdditionalOffers } from './steps/step-3-additional-offers';
import { StepVisualStyle } from './steps/step-4-visual-style';
import { ProductCard } from './ui/product-card';
import { axiosInstanceConversationalEngine } from '../../services/instance';
import { useState, useEffect } from 'react';
import LoadingFluxi from '../common/LoadingFluxi/LoadingFluxi';
import { Toaster, toast } from 'react-hot-toast';
import { FunnelPayload, SaleAngle } from '../../services/interface';
import { postFunnel } from '../../services/funnels';
import { useAuth } from '../../context/AuthContext';
import { dictionary } from '../../utils/dictionary';
import { CreditsBadge } from '../ui/badge/CreditBadge';
import { membershipAxios } from '../../services/membership/MembershipAxios';

// Add type definition at the top of the file
interface ScrapeResult {
  type: string;
  index: number;
  data: any;
  originalProduct: any;
}

interface ProductCompleteness {
  upsell: {
    enabled: boolean;
    products: any[];
  };
  bumpOffer?: {
    enabled: boolean;
    productName: string;
    price: string;
    description?: string;
    url_images?: string;
    image?: string;
    externalId?: string | null;
    providerId?: string | null;
    isManualEntry: boolean;
    productLink: string;
  };
}

// Custom toast notification function
const customToast = (message: string, title: string = 'Validation Error') => {
  return toast.custom(
    (t) => (
      <div
        className={`${t.visible ? 'animate-enter' : 'animate-leave'
          } max-w-md w-full bg-white shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5`}
        style={{
          transition: 'all 0.5s ease-out',
          transform: t.visible ? 'translateX(0)' : 'translateX(100%)'
        }}
      >
        <div className="p-4 w-full flex items-center">
          <div className="flex-shrink-0">
            <div className="h-8 w-8 bg-red-100 rounded-full flex items-center justify-center">
              <X className="h-5 w-5 text-red-500" />
            </div>
          </div>
          <div className="ml-3 flex-1">
            <p className="text-lg font-medium text-gray-900">
              {title}
            </p>
            <p className="mt-1 text-gray-600">
              {message}
            </p>
          </div>
        </div>
      </div>
    ),
    { duration: 4000 }
  );
};

export function OnboardingLayout() {
  const { language, credits, setCredits } = useAuth();
  const { data, nextStep, previousStep, updateProductData, updateVisualContent, updateAdditionalOffers, resetOnboardingData, salesAngles, contextData } = useOnboardingStore();
  const { step } = data;
  // const step = 4;
  const [isLoading, setIsLoading] = useState(false);
  const [productCompleteness, setProductCompleteness] = useState<ProductCompleteness | null>(data.additionalOffers);

  // Reset onboarding data when component mounts

  useEffect(() => {
    setProductCompleteness(data.additionalOffers);
  }, [data.additionalOffers]);

  useEffect(() => {
    resetOnboardingData();
  }, [resetOnboardingData]);

  const handleProductScrape = async () => {
    if (!data.productData.productLink) {
      return true;
    }

    setIsLoading(true);
    let success = false;

    try {
      const response = await axiosInstanceConversationalEngine.post('/scrape-product', {
        product_url: data.productData.productLink
      });

      const productData = response.data.data;

      updateProductData({
        ...data.productData,
        name: productData.name,
        price: productData?.external_sell_price?.toString() || '0',
        details: productData.description,
        isManualEntry: false,
        externalId: productData.external_id,
        providerId: productData.provider_id
      });

      // Update images
      if (productData.images && productData.images.length > 0) {
        updateVisualContent({
          images: productData.images
        });
      }

      // Validate required fields
      if (!productData.name || !productData.description) {
        customToast(dictionary["product-information-incomplete"][language], dictionary["product-information-incomplete-title"][language]);
      } else {
        success = true; // Only mark success if we have all required data
      }
    } catch (err) {
      console.error('Error fetching product:', err);
      customToast('Failed to fetch product data. Please try again.');
      success = false;
    } finally {
      setIsLoading(false);
    }

    return success;
  };

  const handleNextStep = async () => {

    if (step === 1) {
      // Only run scraping if there's a product link AND product data is incomplete
      if (data.productData.productLink &&
        (!data.productData.name || !data.productData.details || !data.visualContent.images?.length)) {
        const scrapingSuccessful = await handleProductScrape();
        if (scrapingSuccessful) {
          nextStep();
        }
        return;
      }

      // Always validate required fields before proceeding
      if (!data.productData.name || !data.productData.details || (!data.visualContent.images?.length && !data.visualContent.imagesBase64?.length)) {
        customToast(dictionary["product-information-incomplete"][language], dictionary["product-information-incomplete-title"][language]);
        return; // Prevent proceeding to next step
      }

      nextStep();
    } else if (step === 2) {
      // Validate that either a customAngle is filled or a selectedAngle is selected
      if (!data.visualContent.customAngle && !data.visualContent.selectedAngle || data.visualContent.selectedAngle.length < 1) {
        customToast(dictionary["please-select-a-minimum-of-1-marketing-angle"][language], dictionary["marketing-angle-required"][language]);
        return; // Prevent proceeding to next step
      }

      nextStep();
    } else if (step === 3) {
      const shouldScrapeBumpOffer = data.additionalOffers.bumpOffer?.enabled && data.additionalOffers.bumpOffer?.productLink && data.additionalOffers.bumpOffer?.uploadedMethod === UploadedMethodType.LINK;

      const upsellEnabled = data.additionalOffers.upsell?.enabled &&
        data.additionalOffers.upsell.products?.some(p =>
          p.productLink || (p.productName && p.price && p.description && p.image)
        );

        
      setIsLoading(true);

      try {
        // Only execute scraping if shouldScrape is true
        // if (shouldScrape) {
        // Create promises for parallel execution
        const promises = [];

        // Setup order bump scraping promise if needed
        if (shouldScrapeBumpOffer &&
          (!data.additionalOffers.bumpOffer?.isManualEntry ||
            (!data.additionalOffers.bumpOffer.productName &&
              !data.additionalOffers.bumpOffer.price &&
              !data.additionalOffers.bumpOffer.description))) {

          const bumpOfferPromise = axiosInstanceConversationalEngine.post('/scrape-product', {
            product_url: data.additionalOffers.bumpOffer?.productLink || ''
          }).then(response => {
            return { type: 'bumpOffer', data: response.data.data };
          });

          promises.push(bumpOfferPromise);
        }

        // Setup upsell products scraping promises if needed
        if (upsellEnabled) {
          const upsellPromises = data.additionalOffers.upsell.products
            .filter(product => product.productLink && !product.isManualEntry)
            .map((product, index) => {
              return axiosInstanceConversationalEngine.post('/scrape-product', {
                product_url: product.productLink
              }).then(response => {
                return {
                  type: 'upsell',
                  index: index,
                  originalProduct: product,
                  data: response.data.data
                };
              });
            });

          promises.push(...upsellPromises);
        }

        // Execute all scraping requests in parallel
        const results = await Promise.all(promises);

        // Process the results
        const updatedAdditionalOffersNew = { ...data.additionalOffers };
        const updatedUpsellProducts = [...data.additionalOffers.upsell.products];

        results.forEach(result => {
          if (result.type === 'bumpOffer' && data.additionalOffers.bumpOffer) {
            const productData = result.data;
            updatedAdditionalOffersNew.bumpOffer = {
              ...data.additionalOffers.bumpOffer,
              productName: productData.name || '',
              price: productData.external_sell_price ? productData.external_sell_price.toString() : '0',
              description: productData.description || '',
              url_images: productData.images && productData.images.length > 0 ? productData.images[0] : '',
              image: '',
              externalId: productData.external_id || null,
              providerId: productData.provider_id || null,
              isManualEntry: false
            };
          } else if (result.type === 'upsell') {
            const productData = result.data;
            updatedUpsellProducts[result.index] = {
              ...result.originalProduct,
              productName: productData.name,
              price: productData.external_sell_price.toString(),
              description: productData.description,
              image: '',
              url_images: productData.images?.[0] ? [productData.images[0]] : [],
              externalId: productData.external_id || null,
              providerId: productData.provider_id || null,
              isManualEntry: false
            };
          }
        });

        // Update upsell products in the state
        if (upsellEnabled) {
          updatedAdditionalOffersNew.upsell = {
            ...data.additionalOffers.upsell,
            products: updatedUpsellProducts
          };
        }

        // Update the store with all scraped data
        handleProductCompleteness(updatedAdditionalOffersNew);

        if (updatedAdditionalOffersNew.bumpOffer?.enabled) {
          const bumpOffer = updatedAdditionalOffersNew.bumpOffer;
          if (!bumpOffer.productName || !bumpOffer.description || (!bumpOffer.image && !bumpOffer.url_images)) {
            customToast(dictionary["order-bump-information-incomplete"][language]);
            setIsLoading(false);
            return;
          }
        }

        if (updatedAdditionalOffersNew.upsell?.enabled && updatedAdditionalOffersNew.upsell.products?.length > 0) {
          for (const product of updatedAdditionalOffersNew.upsell?.products) {
            if (!product.productName || !product.price || !product.description || (!product.image && !((product.url_images ?? []).length > 0))) {
              customToast(dictionary["upsell-information-incomplete"][language]);
              setIsLoading(false);
              return;
            }
          }
        }

        nextStep();
      } catch (err) {
        console.error('Error fetching product data:', err);
        customToast('Failed to fetch product data. Please try again.');
      } finally {
        setIsLoading(false);
      }
    } else {
      nextStep();
    }
  };

  const handleProductCompleteness = (data: any) => {
    updateAdditionalOffers(data);
    setProductCompleteness(data);
  }

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return <StepProductData />;
      case 2:
        return <StepVisualContent />;
      case 3:
        return <StepAdditionalOffers />;
      case 4:
        return <StepVisualStyle />;
      default:
        return <StepProductData />;
    }
  };

  const isLastStep = step === 4;

  const showProductCard = step > 1;

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      if (parseFloat(data.productData.price) === 0 ) {
        customToast('The main product price cannot be 0.', 'Main Product Price Incomplete');
        setIsLoading(false);
        return;
      }

      const products = [
        {
          external_id: data.productData.externalId || null,
          provider_id: data.productData.providerId || null,
          name: data.productData.name,
          description: data.productData.details,
          price: parseFloat(data.productData.price),
          url_images: data.visualContent.images || [],
          images: data.visualContent.imagesBase64 || [],
          type: "PRINCIPAL"
        }
      ];

      let upsellCount = 1;
      if (productCompleteness && productCompleteness.upsell?.enabled && productCompleteness.upsell.products?.length > 0) {
        productCompleteness.upsell.products.forEach(product => {
          if (parseFloat(product.price) === 0) {
            customToast('The upsell product price cannot be 0.', 'Upsell Product Price Incomplete');
            setIsLoading(false);
            return;
          }

          if (product.productName && product.price) {
            products.push({
              external_id: product.externalId || null,
              provider_id: product.providerId || null,
              name: product.productName,
              description: product.description || "",
              price: parseFloat(product.price) || 0,
              url_images: product.url_images || [],
              images: product.image ? [product.image] : [],
              type: "UP_SELL"
            });
            upsellCount++;
          }
        });
      }

      if (productCompleteness && productCompleteness.bumpOffer?.enabled && productCompleteness.bumpOffer.productName && productCompleteness.bumpOffer.price) {
        if (parseFloat(productCompleteness.bumpOffer.price) === 0) {
          customToast('The order bump price cannot be 0.', 'order bump Price Incomplete');
          setIsLoading(false);
          return;
        }

        products.push({
          external_id: productCompleteness.bumpOffer.externalId || null,
          provider_id: productCompleteness.bumpOffer.providerId || null,
          name: productCompleteness.bumpOffer.productName,
          description: productCompleteness.bumpOffer.description || "",
          price: parseFloat(productCompleteness.bumpOffer.price) || 0,
          url_images: productCompleteness.bumpOffer.url_images ? [productCompleteness.bumpOffer.url_images] : [],
          images: productCompleteness.bumpOffer.image ? [productCompleteness.bumpOffer.image] : [],
          type: "BUMP_OFFER"
        });
      }

      const warrantyDays = data.productData.warranty === 'none' ? 0 :
        data.productData.warranty === 'custom' ?
          parseInt(data.productData.customWarrantyDays) : 30;

      const payload: FunnelPayload = {
        products: products,
        brand_name: data.visualStyle.brandName || "",
        sale_angles: data.visualContent.selectedAngle,
        free_shipping: data.productData.freeShipping,
        warranties: warrantyDays,
        template_design: {
          theme: data.visualContent.selectedAngle[0].name || "default",
          primary_color: data.visualStyle.colorPalette || "#FFFFFF",
          secondary_color: "#FFFFFF"
        },
        data_for_context: {
          pain_detection: contextData.pain_detection,
          buyer_detection: contextData.buyer_detection,
          recommended_sales_angles: contextData.recommended_sales_angles
        }
      };

      const response = await postFunnel(payload);
      window.location.href = window.location.origin + "/funnels"


      // const token = localStorage.getItem('access_token');
      // window.location.href = import.meta.env.VITE_REACT_APP_URL_BUILDER + `/builder/${response.owner_id}/${response.id}?token=${token}`;
    } catch (error) {
      console.error('Error submitting form:', error);
      customToast('There was an error creating your funnels. Please try again.', 'Funnels Creation Failed');
      setIsLoading(false);
    }
  };

  const stepTitles = [dictionary["product-details"][language], dictionary["marketing-angle"][language], dictionary["upsell-options"][language], dictionary["brand-style"][language]];

  const setCreditsToContext = async () => {
    const membership = await membershipAxios();
    setCredits(membership.credits || 0);
  };

  setCreditsToContext();

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <Toaster position="top-right" toastOptions={{
        duration: 4000,
        style: {
          background: '#fff',
          color: '#363636',
          boxShadow: '0 3px 10px rgba(0, 0, 0, 0.1)',
          borderRadius: '8px',
          padding: '16px',
        },
      }} />
      <div className="max-w-5xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold mb-6 flex items-center justify-center gap-2">
          <img
            src="https://www.svgrepo.com/show/190530/magic-wand-wizard.svg"
            alt="Magic wand"
            className="w-7 h-7"
          />
          {dictionary["magical-funnel-creation"][language]}
        </h1>

        <div className="mb-6">
          {/* Step indicators with connector lines - full width layout */}
          <div className="w-full grid grid-cols-4">
            {[1, 2, 3, 4].map((stepNumber, index) => (
              <div key={stepNumber} className="flex flex-col items-center relative">
                <StepIndicator
                  step={stepNumber}
                  currentStep={step}
                  label={stepTitles[index]}
                />

                {/* Connector lines */}
                {index < 3 && (
                  <div className="absolute top-5 left-1/2 w-full h-0.5 z-0">
                    <div
                      className={`h-0.5 w-full ${stepNumber < step ? 'bg-primary-600' : 'bg-gray-300'}`}
                    ></div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* NOTE: Product card is hidden but kept for future reference */}
        {showProductCard && (
          <div className="mb-6">
            <ProductCard />
          </div>
        )}

        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          {renderStepContent()}
        </div>

        <div className="flex justify-between">
          {step !== 1 && (
            <button
              type="button"
              onClick={previousStep}
              className="btn btn-secondary"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              {dictionary["previous"][language]}
            </button>
          )}


          <div className="flex items-center justify-end gap-2 ml-auto">
            <CreditsBadge amount={credits} label={dictionary["credits"][language]} className="bg-[#4F46E51A]" />

            <button
              type="button"
              onClick={isLastStep ? handleSubmit : handleNextStep}
              className={`btn btn-primary ${step === 1 ? 'ml-auto' : ''}`}
              disabled={isLoading}
            >
              {isLastStep ? (
                isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  dictionary["create-funnels"][language]
                )
              ) : (
                <>
                  {isLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      {dictionary["next"][language]}
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}